import React from "react";
import { InfoCardComponent } from "../../../services/types/ComponentTypes";

interface InfoCardProps {
  config: InfoCardComponent;
}

export const InfoCard: React.FC<InfoCardProps> = ({ config }) => {
  const [isHovered, setIsHovered] = React.useState(false);

  const formatValue = (value: string | number, format?: string): string => {
    switch (format) {
      case "currency":
        return new Intl.NumberFormat("en-US", {
          style: "currency",
          currency: "USD",
        }).format(Number(value));

      case "percent":
        return `${Number(value)}%`;

      case "number":
        return new Intl.NumberFormat("en-US").format(Number(value));

      case "string":
      default:
        return String(value);
    }
  };

  const getValueColor = (format?: string): string => {
    switch (format) {
      case "currency":
        return "var(--color-primary-500, #35ba5b)"; // Primary color for money
      case "percent":
        return Number(config.value) > 0 ? "var(--color-primary-500, #35ba5b)" : "#EF4444"; // Primary color for positive, red for negative
      case "number":
        return "#ffffff"; // Blue for numbers
      default:
        return config.style?.textColor || "#1F2937";
    }
  };

  const getWidthClass = () => {
    if (config.style?.widthMode === "wrap") return "w-auto";
    if (config.style?.widthMode === "full") return "w-full";
    return "w-auto";
  };

  const getHeightClass = () => {
    if (config.style?.heightMode === "wrap") return "min-h-fit";
    if (config.style?.heightMode === "full") return "min-h-full";
    return "min-h-fit";
  };

  const baseStyles: React.CSSProperties = {
    padding: config.style?.padding || 16,
    margin: config.style?.margin || 0,
    backgroundColor: config.style?.backgroundColor || "#FFFFFF",
    borderWidth: config.style?.borderWidth || 1,
    borderColor: config.style?.borderColor || "#E5E7EB",
    borderRadius: config.style?.borderRadius || 12,
    fontSize: config.style?.fontSize || 12,
    fontWeight: config.style?.fontWeight || "normal",
  };

  const titleSize = config.style?.fontSize ? config.style.fontSize * 0.875 : 12;
  const valueSize = config.style?.fontSize ? config.style.fontSize * 1.5 : 20;
  const iconSize = config.style?.fontSize ? config.style.fontSize * 1.25 : 18;

  return (
    <div
      className={`flex flex-col gap-2 border transition-shadow duration-200 ${getWidthClass()} ${getHeightClass()} ${
        isHovered ? "shadow-md" : "shadow-sm"
      }`}
      style={baseStyles}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center flex-1">
          {config.icon && (
            <span
              className="mr-2"
              style={{
                fontSize: iconSize,
                color: config.style?.textColor || "#6B7280",
              }}
            >
              {config.icon}
            </span>
          )}
          <h3
            className="m-0 font-semibold uppercase tracking-wide text-xs"
            style={{
              fontSize: titleSize,
              color: config.style?.textColor || "#6B7280",
            }}
          >
            {config.name}
          </h3>
        </div>
      </div>

      <div
        className="m-0 font-bold leading-tight"
        style={{
          fontSize: valueSize,
          color: getValueColor(config.valueFormat),
        }}
      >
        {formatValue(config.value, config.valueFormat)}
      </div>
    </div>
  );
};
